import { Injectable, RendererFactory2 } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TransitionService } from '@uirouter/core';
import { Observable } from 'rxjs';

import { NowContentModal } from 'now-mobile/shared/modal/content/content';
import { NowDescriptiveModal } from 'now-mobile/shared/modal/descriptive/descriptive';
import { NowAlertModal } from 'now-mobile/shared/modal/alert/alert';
import { NowMultiImageModal } from 'now-mobile/shared/modal/image/multi/multi';
import { NowSingleImageModal } from 'now-mobile/shared/modal/image/single/single';

@Injectable({
    providedIn: 'root'
})
export class NowDataModal {
    private dialog: MatDialog;
    private previousFocusElement: HTMLElement;
    private delayDialogClose: boolean;

    constructor(dialog: MatDialog, overlayContainer: OverlayContainer, rendererFactory: RendererFactory2, transition: TransitionService) {
        Object.assign(this, { dialog });

        // angular animations renderer hooks up the logic to disable animations into setProperty
        rendererFactory.createRenderer(undefined, undefined).setProperty(overlayContainer.getContainerElement(), '@.disabled', true);

        // this is blocking the route until the modal has fully closed. this only works because we disabled all animations from the line above.
        transition.onStart({}, () => {
            if (this.dialog.openDialogs.length && !this.delayDialogClose) {
                this.dialog.closeAll();

                return new Promise((resolve: (value?: void | PromiseLike<void>) => void) => setTimeout(resolve, 0));
            }
        });

        transition.onFinish({}, () => {
            this.delayDialogClose = false;
            if (this.dialog.openDialogs.length) {
                this.dialog.closeAll();

                return new Promise((resolve: (value?: void | PromiseLike<void>) => void) => setTimeout(resolve, 0));
            }
        });

        this.dialog.afterAllClosed.subscribe(() => {
            if (this.previousFocusElement) {
                this.previousFocusElement.focus();
                this.previousFocusElement = undefined;
            }
        });
    }

    public descriptive(data: NowDescriptiveModalData): Observable<ModalResponse> {
        return this.open<NowDescriptiveModalData>(this.dialog, NowDescriptiveModal, { data });
    }

    public alert(data: NowAlertModalData): Observable<ModalResponse> {
        return this.open<NowAlertModalData>(this.dialog, NowAlertModal, { data });
    }

    public multiImage(data: NowMultiImageModalData): Observable<ModalResponse> {
        return this.open<NowMultiImageModalData>(this.dialog, NowMultiImageModal, { data });
    }

    public singleImage(data: NowSingleImageModalData): Observable<ModalResponse> {
        return this.open<NowSingleImageModalData>(this.dialog, NowSingleImageModal, { data });
    }

    public content(data: NowContentModalData): Observable<ModalResponse> {
        return this.open<NowContentModalData>(this.dialog, NowContentModal, { data });
    }

    public setPreviousFocusElement(ele: HTMLElement): void {
        this.previousFocusElement = ele;
    }

    /* eslint-disable  @typescript-eslint/no-explicit-any */
    // D => Type definition for the data object
    // R => [OPTIONAL] Type for return data. You can give a strong type now or cast the any to a return type after the subscription
    // C => [OPTIONAL] Component/JS Class. It is inherently determined from the second parameter
    public open<D, R = any, C = any>(modalService: MatDialog, component: ComponentType<C>, dataOptions: MatDialogConfig<D>, delayClose: boolean = false): Observable<R> {

        const dialogReference: MatDialogRef<C> = (modalService || this.dialog).open<C, D>(component, dataOptions);
        this.delayDialogClose = delayClose;

        return dialogReference.afterClosed();
    }
    /* eslint-enable  @typescript-eslint/no-explicit-any */

    public closeAll(): void {
        this.dialog.closeAll();
    }
}
